import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'
import { useSnackbar } from 'notistack'

import { CookieNamesEnum } from '@/enums/CookieNamesEnum'
import { GroupingCacheKeysEnum } from '@/enums/GroupingCacheKeysEnum'
import { UserIdEnum } from '@/enums/UserIdEnum'
import { isCartNotFoundError, toCartError } from '@/helpers/cartNotFoundError'
import {
  getCartErrorMessageKey,
  isBuyableExceeded,
  QUANTITY_BUYABLE_EXCEEDED_KEY,
} from '@/helpers/getCartErrorMessageKey'
import { getCookie } from '@/helpers/getCookie'
import { isB2B } from '@/helpers/isB2B'
import { useCartRequest } from '@/hooks/useCartRequest'
import { useCurrentCart } from '@/hooks/useCurrentCart'
import { usePaymentModes } from '@/hooks/usePaymentModes'
import { b2bCartsClient } from '@/lib/clients/b2bCartsClient'
import { b2bOrdersClient } from '@/lib/clients/b2bOrdersClient'
import { cartAddressesClient } from '@/lib/clients/cartAddressesClient'
import { cartEntriesClient } from '@/lib/clients/cartEntriesClient'
import { cartsClient } from '@/lib/clients/cartsClient'
import { ordersClient } from '@/lib/clients/ordersClient'
import { paymentClient } from '@/lib/clients/paymentClient'
import { useAppropriations } from './useAppropriations'
import { useBaseRequest } from './useBaseRequest'
import { useCartApiMutation } from './useCartApiMutation'
import { useCartId } from './useCartId'
import { useSiteContext } from './useSiteContext'
import { useTranslations } from './useTranslations'

import type {
  AcceptPaymentRequest,
  AddAEntryCodeToACartRequest,
  CreateCartDeliveryAddressRequest,
  CreateCartPaymentAddressRequest,
  GetCartRequest,
  MediqCreateCartEntryRequest,
  RemoveCartEntryRequest,
  RemoveCartRequest,
  RemovePaymentAddressRequest,
  ReplaceCartDeliveryAddressRequest,
  ReplaceCartDeliveryInfoRequest,
  ReplaceCartGuestUserRequest,
  ReplaceOrgCartDeliveryAddressRequest,
  ReplaceOrgCartEntriesRequest,
  UpdateCartEntryRequest,
} from '@/gen/ecom/apis'
import type { Address, Cart, OrderEntry, OrderEntryList, Product } from '@/gen/ecom/models'

const overrides: RequestInit = {
  headers: { 'Content-Type': 'application/json' },
}
export function useCart() {
  const queryClient = useQueryClient()
  const { status } = useSession()
  const { cartBaseRequest } = useBaseRequest()
  const { siteContext } = useSiteContext()
  const { invalidateCurrentCacheQuery, isCurrentCartId, isCurrentCartUnknown, loadCurrentCartId, setCurrentCart } =
    useCurrentCart()
  const { invalidate: invalidateAppropriationQuery } = useAppropriations()
  const { paymentModesCacheKey } = usePaymentModes()
  const { enqueueSnackbar } = useSnackbar()
  const { translate } = useTranslations()
  const { setCartId } = useCartId()

  const { cartReq, cartCacheKey } = useCartRequest()
  const translationKeyBase = 'cart.update'
  const showCartMutationError = async (error: unknown) => {
    const messageKey = await getCartErrorMessageKey(error, `${translationKeyBase}.error`)
    if (messageKey) {
      enqueueSnackbar(translate(messageKey), { variant: 'error' })
    }
  }

  // Tracking is emitted by callers via useTrackEvent, not here
  const createCartEntry = ({ req }: { req: MediqCreateCartEntryRequest }) =>
    cartEntriesClient.mediqCreateCartEntry(req, overrides)

  const addProductMutation = useCartApiMutation({
    func: createCartEntry,
    onSuccess: (response) => {
      if (!isBuyableExceeded(response.statusCode)) {
        enqueueSnackbar(translate('cart.add.success'), { variant: 'success' })
      } else if (response.quantityAdded != null && response.quantityAdded > 0) {
        enqueueSnackbar(translate('cart.add.quantity.buyable.exceeded.adjusted.to.max'), {
          variant: 'warning',
        })
      } else {
        enqueueSnackbar(translate(QUANTITY_BUYABLE_EXCEEDED_KEY), {
          variant: 'error',
        })
      }

      return invalidateRelatedQueriesOnMutation()
    },
    onError: showCartMutationError,
  })
  const addB2CProductMutation = useCartApiMutation({
    func: createCartEntry,
    onSuccess: () => invalidateRelatedQueriesOnMutation(),
  })
  const addB2BProductMutation = useCartApiMutation({
    func: ({ req }: { req: ReplaceOrgCartEntriesRequest }) => {
      return b2bCartsClient.replaceOrgCartEntries(req, overrides)
    },
    translationKeyBase: 'cart.add',
  })

  const addB2BProductsAllMutation = useCartApiMutation({
    func: ({ req }: { req: ReplaceOrgCartEntriesRequest }) => {
      return b2bCartsClient.replaceOrgCartEntries(req, overrides)
    },
    onSuccess: ({ cartModifications }) => {
      const addedQuantity =
        cartModifications?.reduce((total, modification) => total + (modification.quantity ?? 0), 0) ?? 0
      if (addedQuantity > 0) {
        enqueueSnackbar(translate('cart.addAll.success'), {
          variant: 'success',
        })
      }

      return invalidateRelatedQueriesOnMutation()
    },
  })

  const removeProductMutation = useCartApiMutation({
    func: ({ req }: { req: RemoveCartEntryRequest }) => cartEntriesClient.removeCartEntry(req, overrides),
    translationKeyBase: 'cart.remove',
    onSuccess: () => {
      return invalidateRelatedQueriesOnMutation()
    },
  })

  const updateProductMutation = useCartApiMutation({
    func: ({ req }: { req: UpdateCartEntryRequest }) => cartEntriesClient.updateCartEntry(req, overrides),
    onSuccess: (response) => {
      if (isBuyableExceeded(response.statusCode)) {
        enqueueSnackbar(translate('cart.add.quantity.buyable.exceeded.adjusted.to.max'), {
          variant: 'warning',
        })
        return
      }

      if (response.statusCode === 'maxOrderQuantityExceeded') {
        enqueueSnackbar(translate('cart.add.quantity.max.order.exceeded'), {
          variant: 'warning',
        })
        return
      }

      enqueueSnackbar(translate(`${translationKeyBase}.success`), { variant: 'success' })
      return invalidateRelatedQueriesOnMutation()
    },

    onError: showCartMutationError,
  })

  const b2cUpdateCartAddressMutation = useCartApiMutation({
    func: ({ req }: { req: ReplaceCartDeliveryAddressRequest }) => {
      return cartAddressesClient.replaceCartDeliveryAddress(req, overrides)
    },
    translationKeyBase: 'cart.address.update',
  })

  const b2bUpdateCartAddressMutation = useCartApiMutation({
    func: ({ req }: { req: ReplaceOrgCartDeliveryAddressRequest }) => {
      return b2bCartsClient.replaceOrgCartDeliveryAddress(req, overrides)
    },
    translationKeyBase: 'cart.address.update',
  })

  const createCartAddressMutation = useCartApiMutation({
    func: ({ req }: { req: CreateCartDeliveryAddressRequest }) => {
      return cartAddressesClient.createCartDeliveryAddress(req, overrides)
    },
    translationKeyBase,
  })

  const createCartBillingAddressMutation = useCartApiMutation({
    func: ({ req }: { req: CreateCartPaymentAddressRequest }) => {
      return cartAddressesClient.createCartPaymentAddress(req, overrides)
    },
    translationKeyBase,
  })

  const removeCartBillingAddressMutation = useCartApiMutation({
    func: ({ req }: { req: RemovePaymentAddressRequest }) => {
      return cartAddressesClient.removePaymentAddress(req, overrides)
    },
    translationKeyBase,
  })

  const createGuestUserMutation = useCartApiMutation({
    func: async ({ req }: { req: ReplaceCartGuestUserRequest }) => cartsClient.replaceCartGuestUser(req, overrides),
    translationKeyBase,
  })

  const deleteMutation = useCartApiMutation({
    func: async ({ req }: { req: RemoveCartRequest }) => {
      return cartsClient.removeCart(req, overrides)
    },
    invalidateQueries: false,
  })

  const updateDeliveryInfoMutation = useCartApiMutation({
    func: async ({ req }: { req: ReplaceCartDeliveryInfoRequest }) =>
      cartsClient.replaceCartDeliveryInfo(req, overrides),
    translationKeyBase,
  })

  const setEntryCodeMutation = useCartApiMutation({
    func: async ({ req }: { req: AddAEntryCodeToACartRequest }) => cartsClient.addAEntryCodeToACart(req, overrides),
    translationKeyBase,
  })

  const setPaymentModeMutation = useCartApiMutation({
    func: ({ paymentModeCode }: { paymentModeCode: string }) => {
      if (!isB2B(siteContext)) {
        return cartsClient.setPaymentMode({ ...cartBaseRequest, paymentModeCode }, overrides)
      }

      return b2bCartsClient.setOrgPaymentMode({ ...cartBaseRequest, paymentModeCode }, overrides)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartCacheKey })
    },
    translationKeyBase,
  })

  const initiatePayment = () => {
    return paymentClient.initiatePayment({ ...cartBaseRequest }, overrides)
  }

  const cancelPayment = () => {
    return paymentClient.cancelPayment({ ...cartBaseRequest }, overrides)
  }

  const acceptPayment = (handleId: string) => {
    const { cartId: _, ...baseRequestWithoutCartId } = cartBaseRequest
    const paymentRequest: AcceptPaymentRequest = { ...baseRequestWithoutCartId, handleId }

    return paymentClient.acceptPayment(paymentRequest, overrides)
  }
  const addOrderRequisition = (requisitionNum: string, orderCode?: string) => {
    const trimmedRequisition = requisitionNum.trim()
    if (trimmedRequisition.length === 0) {
      return Promise.resolve()
    }
    if (!orderCode) {
      return Promise.reject(translate('cart.update.error'))
    }

    return ordersClient.addACommentToOrder1(
      { ...cartBaseRequest, rekvisition: trimmedRequisition, code: orderCode },
      overrides
    )
  }
  const placeOrder = async () => {
    if (isB2B(siteContext)) {
      return b2bOrdersClient.placeOrgOrder({ ...cartBaseRequest }, overrides)
    }

    return ordersClient.mediqPlaceOrder({ ...cartBaseRequest }, overrides)
  }

  const isGuest = cartBaseRequest.userId === UserIdEnum.Anonymous

  const createCurrentCart = async () => {
    const { baseSiteId, userId } = cartBaseRequest
    const newCart = await cartsClient.createCart({ baseSiteId, userId }, overrides)
    makeCartCurrent(newCart)

    return newCart
  }

  const resolveCartIdToAddTo = async (): Promise<string> => {
    const cartId = cartBaseRequest.cartId ?? (isGuest ? (await createCurrentCart()).guid : await loadCurrentCartId())

    if (cartId == null) {
      throw new Error('There is no cart to add products to')
    }

    return cartId
  }

  const recoverFromMissingCart = async (missingCartId: string) => {
    if (isGuest) {
      if (getCookie(CookieNamesEnum.Cart) === missingCartId) {
        setCartId('')
      }

      return
    }

    if ((await loadCurrentCartId()) === missingCartId) {
      await startNewCart()
    }
  }

  const getCart = ({ signal }: { signal: AbortSignal }): Promise<Cart> =>
    cartsClient.getCart(cartReq, { ...overrides, signal }).catch(async (error: unknown) => {
      const cartError = await toCartError(error, cartReq.cartId)
      if (isCartNotFoundError(cartError)) {
        await recoverFromMissingCart(cartError.cartId)
      }

      throw cartError
    })

  const addProduct = async (productCode?: string, quantity?: number) => {
    const product: Product = { code: productCode }
    const orderEntry: OrderEntry = { quantity, product }
    const cartRequest = { ...cartBaseRequest, cartId: await resolveCartIdToAddTo() }
    if (isB2B(siteContext)) {
      const orderEntryList: OrderEntryList = { orderEntries: [orderEntry] }
      const req: ReplaceOrgCartEntriesRequest = {
        ...cartRequest,
        orderEntryList,
      }
      await addB2BProductMutation.mutateAsync({ req })
    } else {
      const req: MediqCreateCartEntryRequest = {
        ...cartRequest,
        orderEntry,
      }
      await addProductMutation.mutateAsync({ req })
    }
  }
  const addB2CProductsAll = async (cartRequest: GetCartRequest, orderEntries: OrderEntry[]) => {
    let totalQuantityAdded = 0
    for (const orderEntry of orderEntries) {
      const req: MediqCreateCartEntryRequest = { ...cartRequest, orderEntry }
      const response = await addB2CProductMutation.mutateAsync({ req })
      if (response.quantityAdded != null && response.quantityAdded > 0) {
        totalQuantityAdded += response.quantityAdded
      }
    }

    return totalQuantityAdded
  }
  const addProductAll = async (orders: { productCode: string; quantity: number }[]) => {
    const orderEntries: OrderEntry[] = orders.map((order) => ({
      quantity: order.quantity,
      product: { code: order.productCode },
    }))

    const cartRequest = { ...cartBaseRequest, cartId: await resolveCartIdToAddTo() }

    if (isB2B(siteContext)) {
      const req: ReplaceOrgCartEntriesRequest = {
        ...cartRequest,
        orderEntryList: { orderEntries },
      }
      await addB2BProductsAllMutation.mutateAsync({ req })

      return
    }

    const totalQuantityAdded = await addB2CProductsAll(cartRequest, orderEntries)
    if (totalQuantityAdded > 0) {
      enqueueSnackbar(translate('cart.addAll.success'), { variant: 'success' })

      return totalQuantityAdded
    }
  }

  const removeProduct = async (entryNumber: number) => {
    const req: RemoveCartEntryRequest = {
      ...cartBaseRequest,
      entryNumber,
    }

    await removeProductMutation.mutateAsync({ req })
  }

  const updateProductQuantity = async (entry: OrderEntry, quantity: number) => {
    if (entry.entryNumber == null) {
      return Promise.reject('No entry number provided')
    }

    const req: UpdateCartEntryRequest = {
      ...cartBaseRequest,
      entryNumber: entry.entryNumber,
      orderEntry: { ...entry, quantity },
    }

    return await updateProductMutation.mutateAsync({ req })
  }

  const b2cUpdateCartAddress = useCallback(
    async (addressId: string) => {
      const req: ReplaceCartDeliveryAddressRequest = {
        ...cartBaseRequest,
        addressId,
      }

      await b2cUpdateCartAddressMutation.mutateAsync({ req })
    },
    [cartBaseRequest, b2cUpdateCartAddressMutation]
  )

  const b2bUpdateCartAddress = useCallback(
    async (addressId: string) => {
      const req: ReplaceOrgCartDeliveryAddressRequest = {
        ...cartBaseRequest,
        addressId,
      }

      await b2bUpdateCartAddressMutation.mutateAsync({ req })
    },
    [cartBaseRequest, b2bUpdateCartAddressMutation]
  )

  const updateCartAddress = (addressId: string) =>
    isB2B(siteContext) ? b2bUpdateCartAddress(addressId) : b2cUpdateCartAddress(addressId)

  const createCartAddress = async (address: Address) => {
    const req: CreateCartDeliveryAddressRequest = {
      ...cartBaseRequest,
      address,
    }

    return createCartAddressMutation.mutateAsync({ req })
  }

  const createCartBillingAddress = async (address: Address) => {
    const req: CreateCartPaymentAddressRequest = {
      ...cartBaseRequest,
      address,
    }

    return await createCartBillingAddressMutation.mutateAsync({ req })
  }

  const removeCartBillingAddress = async (id: Address['id']) => {
    if (id == null) {
      return Promise.reject('No address id provided')
    }

    const req: RemovePaymentAddressRequest = {
      ...cartBaseRequest,
      id,
    }

    return await removeCartBillingAddressMutation.mutateAsync({ req })
  }

  const createGuestUser = async (email: string) => {
    const req: ReplaceCartGuestUserRequest = {
      ...cartBaseRequest,
      email,
    }

    return await createGuestUserMutation.mutateAsync({ req })
  }

  const updateDeliveryInfo = async (deliveryInfo: ReplaceCartDeliveryInfoRequest['deliveryInfo']) => {
    const req: ReplaceCartDeliveryInfoRequest = {
      ...cartBaseRequest,
      deliveryInfo,
    }

    return await updateDeliveryInfoMutation.mutateAsync({ req })
  }

  const setEntryCode = async (entryCode: string) => {
    const req: AddAEntryCodeToACartRequest = {
      ...cartBaseRequest,
      entryCode,
    }

    return await setEntryCodeMutation.mutateAsync({ req })
  }

  const clearCartCache = (): Cart | undefined => {
    const previousCart = queryClient.getQueryData<Cart>(cartCacheKey)
    queryClient.setQueryData<Cart>(cartCacheKey, {
      ...previousCart,
      entries: [],
      totalItems: 0,
      totalUnitCount: 0,
    })

    return previousCart
  }

  const restoreCartCache = (previousCart: Cart | undefined) => {
    queryClient.setQueryData<Cart | undefined>(cartCacheKey, previousCart)
  }

  const deleteCart = async () => {
    const req: RemoveCartRequest = {
      ...cartBaseRequest,
    }
    const previousCart = clearCartCache()

    try {
      await deleteMutation.mutateAsync({ req })
    } catch (error) {
      restoreCartCache(previousCart)
      throw error
    }

    await switchToNewCurrentCart()
  }

  const hasCartId = cartBaseRequest.cartId != null
  const cartQuery = useQuery({
    queryKey: cartCacheKey,
    queryFn: getCart,
    enabled: () => status !== 'loading' && isCurrentCartId(cartBaseRequest.cartId),
    staleTime: Number.POSITIVE_INFINITY,
  })
  // Never report an unknown cart as empty: a failed or not-yet-resolved request is not an empty cart.
  const isCartUnknown = isCurrentCartUnknown || (hasCartId && cartQuery.data == null)

  const validateCurrentCart = async () => await cartsClient.validateCart(cartBaseRequest, overrides)

  const invalidateCartQuery = async () => await queryClient.invalidateQueries({ queryKey: cartCacheKey })

  const makeCartCurrent = (newCart: Cart) => {
    if (cartBaseRequest.userId !== UserIdEnum.Anonymous) {
      setCurrentCart(newCart)
    } else if (newCart.guid != null) {
      setCartId(newCart.guid)
    }
  }

  const startNewCart = async () => {
    try {
      await createCurrentCart()
    } catch {
      await invalidateCurrentCacheQuery()
    }
  }

  const switchToNewCurrentCart = async () => {
    const deletedCartId = cartBaseRequest.cartId
    await startNewCart()
    await queryClient.invalidateQueries({
      queryKey: [GroupingCacheKeysEnum.Cart],
      predicate: ({ queryKey }) => !queryKey.includes(deletedCartId),
      refetchType: 'none',
    })
  }

  const invalidateRelatedQueriesOnMutation = async (options?: { refetchAppropriations?: boolean }) => {
    const { refetchAppropriations = true } = options ?? {}

    await invalidateCurrentCacheQuery()
    await queryClient.invalidateQueries({ queryKey: paymentModesCacheKey, refetchType: 'none' })
    await invalidateAppropriationQuery({ skipRefetch: !refetchAppropriations })
  }

  return {
    addOrderRequisition,
    addProductAll,
    addProduct,
    cart: cartQuery.data,
    createCartAddress,
    createCartBillingAddress,
    removeCartBillingAddress,
    createGuestUser,
    setPaymentModeMutation,
    isCartUnknown,
    isEmptyCart: !isCartUnknown && !cartQuery.data?.entries?.length,
    isLoadingCart: cartQuery.isLoading || cartQuery.isFetching,
    removeProduct,
    deleteCart,
    updateCartAddress,
    updateProductQuantity,
    validateCurrentCart,
    placeOrder,
    initiatePayment,
    acceptPayment,
    cancelPayment,
    invalidateCartQuery,
    updateDeliveryInfo,
    setEntryCode,
  }
}
